/**
 * Customer API client. Thin fetch wrapper that:
 *   - Prepends the app-api base URL.
 *   - Injects `Authorization: Bearer <jwt>` when a token is in
 *     sessionStorage.
 *   - On 401, attempts a single silent-refresh round-trip (cookie-
 *     backed `__Host-` HttpOnly refresh handle on app-api) and retries.
 *   - On 401 again, surfaces the error so RequireAuth can prompt
 *     popup-reauth.
 *
 * Mirrors `apps/admin-console-spa/src/lib/api-client.ts` but uses
 * sessionStorage + cookies instead of MSAL silent acquire.
 */

const TOKEN_STORAGE_KEY = 'wf-coe-app:jwt';
/**
 * Refresh-handle storage key. The handle is an opaque DDB row key
 * (UUID) — server-side it indexes into the refresh-token table; in
 * the SPA it's just a string we send back to `/auth/silent-refresh`
 * as `Authorization: Bearer ...`. We use localStorage (not session)
 * because the JWT clears on tab close by design and silent-refresh
 * is precisely the recovery path for "new tab, no JWT" — needs to
 * survive across tabs and reloads. 30-day server-side TTL matches
 * the legacy refresh cookie's Max-Age.
 */
const REFRESH_HANDLE_STORAGE_KEY = 'wf-coe-app:refresh-handle';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
  put<T>(path: string, body: unknown): Promise<T>;
  patch<T>(path: string, body: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
  /**
   * GET a binary response — used by routes that return docx, PDFs,
   * or other non-JSON content. Returns the raw {@link Blob} so the
   * caller can `URL.createObjectURL` → `<a>` download trigger.
   *
   * Auth + silent-refresh follow the same path as {@link get}. The
   * `Accept` header is set to `*\/*` so the server doesn't try to
   * negotiate JSON, and the suggested filename (from
   * `Content-Disposition`) is returned alongside so the caller
   * doesn't have to reparse the header.
   */
  getBlob(path: string): Promise<{ blob: Blob; filename: string | null }>;
}

/**
 * Fire the `/auth/silent-refresh` round-trip directly. Prefers the
 * locally-stored opaque refresh handle as `Authorization: Bearer …`
 * (set on popup-OAuth completion via `setStoredRefreshHandle`) so
 * the refresh works in cross-partition contexts where the
 * `__Host-` cookie isn't sent (popup → opener boundary under CHIPS,
 * localhost dev, etc.). Falls back to the cookie-only path via
 * `credentials: 'include'` for legacy SPA bundles already in the
 * wild that haven't picked up the new handle.
 *
 * Returns the freshly minted JWT or `null` if the handle is missing
 * / expired / revoked. Exposed so `AuthProvider` can use it on
 * mount + tab focus + the proactive pre-expiry timer.
 */
export async function silentRefresh(baseUrl: string): Promise<string | null> {
  try {
    const handle = getStoredRefreshHandle();
    const headers: Record<string, string> = {};
    if (handle) headers.Authorization = `Bearer ${handle}`;
    const response = await fetch(`${baseUrl.replace(/\/+$/, '')}/auth/silent-refresh`, {
      method: 'POST',
      credentials: 'include',
      headers,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { token?: string };
    return typeof data.token === 'string' && data.token.length > 0 ? data.token : null;
  } catch {
    return null;
  }
}

export interface ApiClientOptions {
  /** App-API base URL — e.g. `https://app-api.workfocus.dev`. */
  baseUrl: string;
  /**
   * Called on 401 to trigger interactive re-auth. The client surfaces
   * the error after this resolves; the caller decides what UX to
   * present (popup-OAuth from a button click, fallback to redirect,
   * etc.).
   */
  onAuthRequired?: () => void;
}

/**
 * JWT storage. Prefers sessionStorage so the token clears with the
 * browser tab — the right default for an iframe-embedded customer
 * tool where shared-laptop scenarios are the dominant access
 * pattern. Falls back to localStorage when sessionStorage throws
 * (Chrome incognito with third-party cookies blocked, Safari ITP
 * partitioned storage edge cases, some extension policies). The
 * fallback is per-domain shared across tabs, which is slightly
 * less restrictive but always beats the user staring at "Sign in"
 * with no working storage.
 *
 * Writes go to BOTH stores opportunistically so the cleanup path
 * doesn't leak a stale value if storage mode changed between sets.
 */
function tryStorage(kind: 'session' | 'local'): Storage | null {
  try {
    const s = kind === 'session' ? window.sessionStorage : window.localStorage;
    // Probe — some browsers throw on access for blocked storage.
    const probeKey = '__wf-coe-probe__';
    s.setItem(probeKey, '1');
    s.removeItem(probeKey);
    return s;
  } catch {
    return null;
  }
}

export function getStoredToken(): string | null {
  const session = tryStorage('session');
  const local = tryStorage('local');
  return session?.getItem(TOKEN_STORAGE_KEY) ?? local?.getItem(TOKEN_STORAGE_KEY) ?? null;
}

export function setStoredToken(token: string | null): void {
  const session = tryStorage('session');
  const local = tryStorage('local');
  if (token === null) {
    session?.removeItem(TOKEN_STORAGE_KEY);
    local?.removeItem(TOKEN_STORAGE_KEY);
    return;
  }
  // Prefer sessionStorage; only fall back to localStorage when
  // sessionStorage is unavailable.
  if (session) {
    session.setItem(TOKEN_STORAGE_KEY, token);
    // Clear any stale localStorage entry from a prior fallback.
    local?.removeItem(TOKEN_STORAGE_KEY);
    return;
  }
  if (local) {
    local.setItem(TOKEN_STORAGE_KEY, token);
  }
  /* both unavailable — silent-refresh on every request will kick in */
}

/**
 * Refresh-handle storage. Always uses localStorage (unlike the JWT
 * which prefers sessionStorage) so it survives a tab close and
 * silent-refresh can recover the session on the next mount.
 */
export function getStoredRefreshHandle(): string | null {
  const local = tryStorage('local');
  return local?.getItem(REFRESH_HANDLE_STORAGE_KEY) ?? null;
}

export function setStoredRefreshHandle(handle: string | null): void {
  const local = tryStorage('local');
  if (!local) return;
  if (handle === null) {
    local.removeItem(REFRESH_HANDLE_STORAGE_KEY);
    return;
  }
  local.setItem(REFRESH_HANDLE_STORAGE_KEY, handle);
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '');

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = getStoredToken();
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;

    const url = `${baseUrl}${path}`;
    let response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      // Sends the __Host- refresh cookie cross-origin so silent-refresh
      // can pick it up. CORS on app-api allows credentials for the
      // configured allowOrigins (the SPA's origin).
      credentials: 'include',
    });

    if (response.status === 401) {
      // One refresh attempt. /auth/silent-refresh prefers an
      // `Authorization: Bearer <handle>` header (post-CHIPS), and
      // falls back to the cookie when the handle isn't stored
      // (older session bootstrapped before the Bearer rollout).
      const handle = getStoredRefreshHandle();
      const refreshHeaders: Record<string, string> = {};
      if (handle) refreshHeaders.Authorization = `Bearer ${handle}`;
      const refreshResponse = await fetch(`${baseUrl}/auth/silent-refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: refreshHeaders,
      });
      if (refreshResponse.ok) {
        const data = (await refreshResponse.json()) as { token?: string };
        if (data.token) {
          setStoredToken(data.token);
          headers.Authorization = `Bearer ${data.token}`;
          response = await fetch(url, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
            credentials: 'include',
          });
        }
      }
      if (response.status === 401) {
        setStoredToken(null);
        setStoredRefreshHandle(null);
        options.onAuthRequired?.();
      }
    }

    if (!response.ok) {
      let payload: unknown;
      try {
        payload = await response.json();
      } catch {
        payload = await response.text().catch(() => undefined);
      }
      throw new ApiError(
        response.status,
        `API ${method} ${path} failed: ${response.status}`,
        payload,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  /**
   * Binary GET — mirrors `request` for auth + silent-refresh, but
   * keeps the response as a Blob instead of `JSON.parse`-ing it.
   * Used by the scenario-document export and any other route that
   * returns docx / pdf / arbitrary bytes.
   */
  async function requestBlob(path: string): Promise<{ blob: Blob; filename: string | null }> {
    const token = getStoredToken();
    const headers: Record<string, string> = { Accept: '*/*' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const url = `${baseUrl}${path}`;
    let response = await fetch(url, { method: 'GET', headers, credentials: 'include' });

    if (response.status === 401) {
      // Single silent-refresh attempt — same shape as the JSON path
      // above. Duplicating the refresh dance keeps the binary path
      // independent of the JSON path's response parsing.
      const handle = getStoredRefreshHandle();
      const refreshHeaders: Record<string, string> = {};
      if (handle) refreshHeaders.Authorization = `Bearer ${handle}`;
      const refreshResponse = await fetch(`${baseUrl}/auth/silent-refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: refreshHeaders,
      });
      if (refreshResponse.ok) {
        const data = (await refreshResponse.json()) as { token?: string };
        if (data.token) {
          setStoredToken(data.token);
          headers.Authorization = `Bearer ${data.token}`;
          response = await fetch(url, {
            method: 'GET',
            headers,
            credentials: 'include',
          });
        }
      }
      if (response.status === 401) {
        setStoredToken(null);
        setStoredRefreshHandle(null);
        options.onAuthRequired?.();
      }
    }

    if (!response.ok) {
      // Error responses on the engine are JSON `{ error: '...' }` —
      // try to parse, fall back to plain text for the message.
      let payload: unknown;
      try {
        payload = await response.json();
      } catch {
        payload = await response.text().catch(() => undefined);
      }
      throw new ApiError(response.status, `API GET ${path} failed: ${response.status}`, payload);
    }
    const blob = await response.blob();
    return {
      blob,
      filename: parseContentDispositionFilename(response.headers.get('Content-Disposition')),
    };
  }

  return {
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    put: (path, body) => request('PUT', path, body),
    patch: (path, body) => request('PATCH', path, body),
    delete: (path) => request('DELETE', path),
    getBlob: (path) => requestBlob(path),
  };
}

/**
 * Pull `filename="..."` out of a `Content-Disposition` header.
 * Handles plain `filename="x.docx"` and the RFC-5987 `filename*=UTF-8''...`
 * variant (URL-decoded). Returns `null` when neither is present or
 * the header is malformed — caller falls back to a synthesised name.
 */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=([^']*)'[^']*'([^;]+)/i.exec(header);
  if (star && star[2]) {
    try {
      return decodeURIComponent(star[2].trim());
    } catch {
      // fall through to the plain form
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain && plain[1] ? plain[1].trim() : null;
}
